import styled from "styled-components";
import CardsList from "ui/List";
import ToogleTabs from "ui/ToogleTabs";
import Heading from "ui/Text/Heading";
import StreetCard from "../../../../ui/List/Street/Card";
import { GenericStreetSection } from "interfaces/content";
import Body from "ui/Text/Body";
import { spacing } from "globalStyles";
import { breakpoints } from "themes/breakpoints";
import ComingSoon from "ui/ComingSoon";
import { useMemo } from "react";
import { SubscriptionClient } from "./SubscriptionClient";
import FlexBox from "ui/FlexBox";

interface StreetProps {
  data: GenericStreetSection["data"];
}

const Street = ({ data }: StreetProps) => {
  const tabOptions = useMemo(() => {
    if (!data.tabs?.length) {
      return null;
    }

    return data.tabs.map((tab) => ({
      label: tab.label,
      value: tab.label,
      items: [],
      image: tab?.image,
    }));
  }, [data.tabs]);

  return (
    <Container
      id={data.id}
      $backgroundColor={data.config?.backgroundColor}
      className="anchor-offset"
    >
      <div className="content-container">
        {data.title && (
          <Heading align="center" variant="h2" className="section-title" color="primary">
            {data.title}
          </Heading>
        )}

        {data.description && (
          <Body variant="body4" color="secondary">
            {data.description}
          </Body>
        )}

        {data.info && (
          <Body className="mt-xl" variant="body3" color="secondary" align="center">
            {data.info}
          </Body>
        )}

        {tabOptions && (
          <ToogleTabs
            title={data.titleTab}
            buttonWidth={386}
            buttonHeight={40}
            tabs={tabOptions}
          />
        )}

        {data.items?.length ? (
          <>
            <CardsList
              items={data.items}
              elementKey="name"
              className={data.config?.cardMargin || "mt-lg"}
              renderItem={(item) => (
                <StreetCard street={item} cellConfig={data.cellConfig} />
              )}
            />

            {data.complementaryText && (
              <ComplementaryText className="mt-md" variant="body6" color="secondary">
                {data.complementaryText}
              </ComplementaryText>
            )}
          </>
        ) : (
          <ComingSoon className="mt-xl" />
        )}

        {data.subscriptionClient && (
          <FlexBox className="mt-xl">
            <SubscriptionClient {...data.subscriptionClient} />
          </FlexBox>
        )}

        {data.complementaryInfo && (
          <>
            <Heading className="mt-xl" align="center" variant="h5" color="secondary">
              {data.complementaryInfo.title}
            </Heading>
            <Body
              className="mt-sm mb-lg"
              variant="body3"
              color="secondary"
              align="center"
            >
              {data.complementaryInfo.subtitle}
            </Body>
            {data.complementaryInfo.image && (
              <Image
                className="mt-lg"
                src={`${process.env.REACT_APP_BASE_RELATIVE_ROUTES}/assets/${data.complementaryInfo.image.path}`}
                alt={data.complementaryInfo.image?.alt}
              />
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default Street;

type ContainerComponentProps = {
  $backgroundColor?: string;
};

const Container = styled.div<ContainerComponentProps>`
  width: 100%;
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor || theme.colors.background.secondary};
`;

const ComplementaryText = styled(Body)`
  margin-top: ${spacing.medium}px;

  @media (max-width: ${breakpoints.md}) {
    margin-top: ${spacing.small}px;
  }
`;

const Image = styled.img`
  width: 100%;
  margin-top: 0px;
`;
